import { MAX_INT32_VALUE } from '@tom-ui/utils'
import { ConfigAreaType, ReeferPlugDirection } from '@yard/app/api'
import { OccupancyStore } from '@yard/stores/OccupancyStore'
import { RoadNetworkStore } from '@yard/stores/road-network.store'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import { getConfigAreaByType } from '@yard/utils/yard-block.util'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import {
  mapYardBlockFormDataToCreateCommand,
  mapYardBlockFormDataToUpdateCommand,
} from '../../helpers/yard-block.mapper'
import { BAY_WIDTH, ROW_HEIGHT } from '../../helpers/yard-view.utils'
import {
  IBayFormData,
  ILimitStackHeightConfigAreaFormData,
  ISpaceBetweenBaysConfigAreaFormData,
  IYardBlockFormData,
} from '../../models/yard-setup.model'
import { BlockAccessPointsViewStore } from './block-access-points.view-store'
import { YardNodeViewStore } from './yard-node.view-store'

export enum YardBlockEditSectionDialogType {
  Bay = 'bay',
  Row = 'row',
  Slot = 'slot',
}

export class YardBlockViewStore extends YardNodeViewStore<IYardBlockFormData, YardBlockStore> {
  isRenameSectionDialogOpen = false
  renameSectionDialogType: YardBlockEditSectionDialogType | null = null
  isLimitStackHeightDialogOpen = false
  limitStackHeightDialogMode: YardBlockEditSectionDialogType | null = null
  isSpaceBetweenBaysDialogOpen = false

  editingBayIndex?: number
  editingRowIndex?: number

  readonly accessPoints: BlockAccessPointsViewStore

  constructor(
    yardBlockStore: YardBlockStore,
    private readonly occupancyStore: OccupancyStore,
    private readonly roadNetworkStore: RoadNetworkStore,
  ) {
    super(yardBlockStore)
    makeObservable(this, {
      editingBayIndex: observable,
      editingRowIndex: observable,
      isRenameSectionDialogOpen: observable,
      renameSectionDialogType: observable,
      isLimitStackHeightDialogOpen: observable,
      limitStackHeightDialogMode: observable,
      isSpaceBetweenBaysDialogOpen: observable,

      setRotation: action,
      setPosition: action,
      setLimitStackHeightDialogMode: action,

      openRenameSectionDialog: action,
      closeRenameSectionDialog: action,
      openLimitStackHeightDialog: action,
      closeLimitStackHeightDialog: action,
      openSpaceBetweenBaysDialog: action,
      closeSpaceBetweenBaysDialog: action,

      focusedYardBlock: computed,
      isFocusedBlockStandard: computed,
      hasFocusedBlockOccupancies: computed,
    })

    this.accessPoints = new BlockAccessPointsViewStore(yardBlockStore, roadNetworkStore)

    yardBlockStore.onBlockDimensionsChanged = (blockId: string) =>
      this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  setRotation = (id: string, rotation: number) => {
    const yardBlock = this.entityStore.yardBlocks[id]
    if (!yardBlock) return

    const thetaNew = Math.round(rotation)
    if (yardBlock.layoutPosition.rotation === thetaNew) return

    const thetaOld = yardBlock.layoutPosition.rotation
    const layoutPosition = { ...yardBlock.layoutPosition, rotation: thetaNew }

    // Optimistically update the rotation in the local store so that rapid successive calls
    // read the correct thetaOld instead of the stale server-synced value.
    this.entityStore.updateStoreItem({ ...yardBlock, layoutPosition }, id)

    this.entityStore.updateLayoutPosition({ ...layoutPosition, yardBlockId: id })
    this.rotateLinkedAccessPointNodes(id, thetaOld, thetaNew)
  }

  private rotateLinkedAccessPointNodes(blockId: string, thetaOld: number, thetaNew: number): void {
    const block = this.entityStore.yardBlocks[blockId]
    if (!block) return

    const delta = (thetaNew - thetaOld) * (Math.PI / 180)
    const W = (block.maxBays + 1) * BAY_WIDTH
    const H = (block.maxRows + 1) * ROW_HEIGHT
    const cx = block.layoutPosition.x + W / 2
    const cy = block.layoutPosition.y + H / 2
    const cos = Math.cos(delta)
    const sin = Math.sin(delta)

    const accessPointIds = new Set((block.accessPoints ?? []).map(ap => ap.id))
    const linkedNodes = this.roadNetworkStore.nodes.filter(
      n => n.blockAccessPointId != null && accessPointIds.has(n.blockAccessPointId),
    )

    for (const node of linkedNodes) {
      const dx = node.layoutPosition.x - cx
      const dy = node.layoutPosition.y - cy
      const newX = Math.round(cx + dx * cos - dy * sin)
      const newY = Math.round(cy + dx * sin + dy * cos)

      if (newX === node.layoutPosition.x && newY === node.layoutPosition.y) continue

      const updatedNode = {
        ...node,
        layoutPosition: { ...node.layoutPosition, x: newX, y: newY },
      }
      this.roadNetworkStore.updateNodeOptimistic(updatedNode)
      this.roadNetworkStore.debouncedUpdateNode(node.id)
    }
  }

  setPosition = (id: string, position: { x: number; y: number }) => {
    const yardBlock = this.entityStore.yardBlocks[id]

    if (
      yardBlock &&
      (yardBlock.layoutPosition.x !== position.x || yardBlock.layoutPosition.y !== position.y)
    ) {
      const layoutPosition = {
        ...yardBlock.layoutPosition,
        x: Math.round(position.x),
        y: Math.round(position.y),
      }

      this.entityStore.updateLayoutPosition({
        ...layoutPosition,
        yardBlockId: id,
      })
    }
  }

  setLimitStackHeightDialogMode = (mode: YardBlockEditSectionDialogType | null) => {
    this.limitStackHeightDialogMode = mode
  }

  openRenameBayDialog = (blockId: string, bayIndex: number) => {
    this.openRenameSectionDialog(YardBlockEditSectionDialogType.Bay, blockId, bayIndex)
  }

  openRenameRowDialog = (blockId: string, rowIndex: number) => {
    this.openRenameSectionDialog(YardBlockEditSectionDialogType.Row, blockId, rowIndex)
  }

  openRenameSectionDialog = (
    dialogType: YardBlockEditSectionDialogType,
    blockId: string,
    index?: number,
  ) => {
    this.isRenameSectionDialogOpen = true
    this.renameSectionDialogType = dialogType
    this.editingId = blockId
    if (dialogType === YardBlockEditSectionDialogType.Bay) {
      this.editingBayIndex = index
      this.editingRowIndex = undefined
    } else if (dialogType === YardBlockEditSectionDialogType.Row) {
      this.editingRowIndex = index
      this.editingBayIndex = undefined
    }
  }

  closeRenameSectionDialog = () => {
    this.isRenameSectionDialogOpen = false
    this.renameSectionDialogType = null
    this.editingId = undefined
    this.editingBayIndex = undefined
    this.editingRowIndex = undefined
  }

  openLimitStackHeightDialog = (
    dialogType: YardBlockEditSectionDialogType,
    blockId: string,
    bay?: number,
    row?: number,
  ) => {
    this.setLimitStackHeightDialogMode(dialogType)
    this.isLimitStackHeightDialogOpen = true
    this.editingId = blockId
    this.editingBayIndex = bay
    this.editingRowIndex = row
  }

  closeLimitStackHeightDialog = () => {
    this.isLimitStackHeightDialogOpen = false
    this.editingId = undefined
    this.editingBayIndex = undefined
    this.editingRowIndex = undefined
    this.setLimitStackHeightDialogMode(null)
  }

  openSpaceBetweenBaysDialog = (blockId: string, bay: number) => {
    this.isSpaceBetweenBaysDialogOpen = true
    this.editingId = blockId
    this.editingBayIndex = bay
  }

  closeSpaceBetweenBaysDialog = () => {
    this.isSpaceBetweenBaysDialogOpen = false
    this.editingId = undefined
    this.editingBayIndex = undefined
  }

  get focusedYardBlock() {
    return this.editingId ? this.entityStore.yardBlocks[this.editingId] : undefined
  }

  get hasFocusedBlockOccupancies(): boolean {
    if (!this.editingId) return false
    return this.occupancyStore.occupanciesByBlock[this.editingId]?.length > 0
  }

  get isFocusedBlockStandard() {
    if (!this.focusedYardBlock) return false
    return this.focusedYardBlock.type === 'Standard'
  }

  async submit(formData: IYardBlockFormData): Promise<void> {
    if (this.focusedYardBlock) {
      const updateCommand = mapYardBlockFormDataToUpdateCommand(this.focusedYardBlock, formData)
      await this.entityStore.updateYardBlock(updateCommand)
    } else {
      const createCommand = mapYardBlockFormDataToCreateCommand(formData, this.viewportCenter)
      await this.entityStore.createYardBlock(createCommand)
    }
  }

  async renameBay(formData: IBayFormData): Promise<void> {
    if (this.editingId === undefined || this.editingBayIndex === undefined) return
    await this.entityStore.renameBay(this.editingId, this.editingBayIndex, formData.name)
  }

  async renameRow(formData: { name: string }): Promise<void> {
    if (this.editingId === undefined || this.editingRowIndex === undefined) return
    await this.entityStore.renameRow(this.editingId, this.editingRowIndex, formData.name)
  }

  async insertBayLeft(blockId: string, bayIndex: number): Promise<void> {
    await this.entityStore.insertBayLeft(blockId, bayIndex)
    this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  async insertBayRight(blockId: string, bayIndex: number): Promise<void> {
    await this.entityStore.insertBayRight(blockId, bayIndex)
    this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  async insertRowUp(blockId: string, rowIndex: number): Promise<void> {
    await this.entityStore.insertRowUp(blockId, rowIndex)
    this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  async insertRowDown(blockId: string, rowIndex: number): Promise<void> {
    await this.entityStore.insertRowDown(blockId, rowIndex)
    this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  async deleteBay(blockId: string, bayIndex: number): Promise<void> {
    await this.entityStore.deleteBay(blockId, bayIndex)
    this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  async deleteRow(blockId: string, rowIndex: number): Promise<void> {
    await this.entityStore.deleteRow(blockId, rowIndex)
    this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  async limitCurrentBayStackHeightConfigArea(data: ILimitStackHeightConfigAreaFormData) {
    if (this.editingBayIndex === undefined || this.editingId === undefined) return

    const bayNumber = this.editingBayIndex

    await this.limitStackHeightConfigArea(
      this.editingId,
      bayNumber,
      bayNumber,
      1,
      MAX_INT32_VALUE,
      data.stackHeight,
    )
  }

  async limitCurrentRowStackHeightConfigArea(data: ILimitStackHeightConfigAreaFormData) {
    if (this.editingRowIndex === undefined || this.editingId === undefined) return

    const rowNumber = this.editingRowIndex

    await this.limitStackHeightConfigArea(
      this.editingId,
      1,
      MAX_INT32_VALUE,
      rowNumber,
      rowNumber,
      data.stackHeight,
    )
  }

  async limitCurrentSlotStackHeightConfigArea(data: ILimitStackHeightConfigAreaFormData) {
    if (
      this.editingBayIndex === undefined ||
      this.editingRowIndex === undefined ||
      this.editingId === undefined
    )
      return

    const bayNumber = this.editingBayIndex
    const rowNumber = this.editingRowIndex

    await this.limitStackHeightConfigArea(
      this.editingId,
      bayNumber,
      bayNumber,
      rowNumber,
      rowNumber,
      data.stackHeight,
    )
  }

  async removeCurrentBayMaxStackHeightConfigArea() {
    if (this.editingBayIndex === undefined || this.editingId === undefined) return

    const bayNumber = this.editingBayIndex

    const currentBay = getConfigAreaByType(
      ConfigAreaType.MaxStackHeight,
      this.focusedYardBlock?.configAreas || [],
      bayNumber,
      bayNumber,
      1,
      MAX_INT32_VALUE,
    )

    if (currentBay) {
      await this.deleteConfigArea(currentBay.id)
    }
  }

  async removeCurrentRowMaxStackHeightConfigArea() {
    if (this.editingRowIndex === undefined || this.editingId === undefined) return

    const rowNumber = this.editingRowIndex

    const currentRow = getConfigAreaByType(
      ConfigAreaType.MaxStackHeight,
      this.focusedYardBlock?.configAreas || [],
      1,
      MAX_INT32_VALUE,
      rowNumber,
      rowNumber,
    )

    if (currentRow) {
      await this.deleteConfigArea(currentRow.id)
    }
  }

  async removeCurrentSlotMaxStackHeightConfigArea() {
    if (
      this.editingBayIndex === undefined ||
      this.editingRowIndex === undefined ||
      this.editingId === undefined
    )
      return

    const bayNumber = this.editingBayIndex
    const rowNumber = this.editingRowIndex

    const currentSlot = getConfigAreaByType(
      ConfigAreaType.MaxStackHeight,
      this.focusedYardBlock?.configAreas || [],
      bayNumber,
      bayNumber,
      rowNumber,
      rowNumber,
    )

    if (currentSlot) {
      await this.deleteConfigArea(currentSlot.id)
    }
  }

  async limitStackHeightConfigArea(
    blockId: string,
    minBay: number,
    maxBay: number,
    minRow: number,
    maxRow: number,
    stackHeight: number,
    description?: string,
  ): Promise<void> {
    if (!this.focusedYardBlock) return

    //If there's already a max stack height config area for the same bay and rows, first delete it before creating a new one with the updated stack height
    const existingConfigArea = getConfigAreaByType(
      ConfigAreaType.MaxStackHeight,
      this.focusedYardBlock.configAreas,
      minBay,
      maxBay,
      minRow,
      maxRow,
    )

    if (existingConfigArea) {
      await this.deleteConfigArea(existingConfigArea.id)
    }

    await this.entityStore.limitStackHeightConfigArea(
      blockId,
      minBay,
      maxBay,
      minRow,
      maxRow,
      stackHeight,
      description,
    )
  }

  async blockConfigArea(
    blockId: string,
    minBay: number,
    maxBay: number,
    minRow: number,
    maxRow: number,
    description?: string,
  ) {
    await this.entityStore.blockConfigArea({
      blockId,
      minBay,
      maxBay,
      minRow,
      maxRow,
      description,
    })
  }

  async deleteConfigArea(configId: string, blockId?: string) {
    await this.entityStore.deleteConfigArea({ id: configId })
    if (blockId) this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  async reeferPlugConfigArea(blockId: string, bay: number, direction: ReeferPlugDirection) {
    await this.entityStore.reeferPlugConfigArea(blockId, bay, direction)
    this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  async spaceBetweenBaysConfigArea(blockId: string, bay: number, widthInMeters: number) {
    await this.entityStore.spaceBetweenBaysConfigArea(blockId, bay, widthInMeters)
    this.accessPoints.recalculateAccessPointNodePositions(blockId)
  }

  async addCurrentSpaceBetweenBays(data: ISpaceBetweenBaysConfigAreaFormData) {
    if (this.editingId === undefined || this.editingBayIndex === undefined) return
    await this.spaceBetweenBaysConfigArea(this.editingId, this.editingBayIndex, data.widthInMeters)
  }

  async deleteSpaceBetweenBaysConfigArea(blockId: string, bay: number) {
    const block = this.entityStore.yardBlocks[blockId]
    if (!block) return
    const sbb = block.configAreas?.find(
      ca => ca.type === ConfigAreaType.SpaceBetweenBays && ca.minBay === bay,
    )
    if (!sbb?.id) return
    await this.deleteConfigArea(sbb.id, blockId)
  }

  async delete(id: string): Promise<void> {
    const accessPoints = this.entityStore.yardBlocks[id]?.accessPoints ?? []
    const accessPointIds = new Set(accessPoints.map(ap => ap.id))
    const linkedNodes = this.roadNetworkStore.nodes.filter(
      n => n.blockAccessPointId != null && accessPointIds.has(n.blockAccessPointId),
    )
    for (const node of linkedNodes) {
      this.roadNetworkStore.removeNodeOptimistic(node.id)
    }
    try {
      await this.entityStore.deleteYardBlock(id)
      await Promise.all(linkedNodes.map(node => this.roadNetworkStore.deleteNode(node.id)))
    } catch (err) {
      runInAction(() => {
        for (const node of linkedNodes) {
          this.roadNetworkStore.addNodeOptimistic(node)
        }
      })
      throw err
    }
  }
}
